import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { getWarehouses } from '../../services/warehouseService';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';

// Material UI icons
import {
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Inventory as InventoryIcon,
  Assessment as AssessmentIcon,
  Warehouse as WarehouseIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';

const InventoryReport = () => {
  const theme = useTheme();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({
    warehouse_id: '',
    part_type: ''
  });

  // Fetch warehouses on component mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const warehousesData = await getWarehouses();
        setWarehouses(warehousesData);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        // Don't set error state here to avoid blocking the report
      }
    };

    fetchWarehouses();
  }, []);

  // Fetch report data when filters change
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query string from filters
        let queryString = '';
        if (filters.warehouse_id) queryString += `warehouse_id=${filters.warehouse_id}&`;
        if (filters.part_type) queryString += `part_type=${filters.part_type}&`;

        // Fetch report data
        const response = await axios.get(`/api/reports/inventory?${queryString}`);
        setReport(response.data);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching inventory report:', err);
        setError('Failed to load inventory report');
        setLoading(false);
      }
    };

    fetchReport();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleExport = async () => {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to export data');
        return;
      }

      // Build query string from filters
      let queryString = '';
      if (filters.warehouse_id) queryString += `warehouse_id=${filters.warehouse_id}&`;
      if (filters.part_type) queryString += `part_type=${filters.part_type}&`;

      // Remove trailing & if present
      if (queryString.endsWith('&')) {
        queryString = queryString.slice(0, -1);
      }

      // Use axios to make a request with the token in the header
      axios({
        url: `/api/reports/export/inventory?${queryString}`,
        method: 'GET',
        responseType: 'blob', // Important for handling file downloads
        headers: {
          'x-auth-token': token
        }
      }).then((response) => {
        // Create a blob URL for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'inventory_report.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch((error) => {
        console.error('Error downloading file:', error);
        alert('Failed to export report');
      });
    } catch (err) {
      console.error('Error exporting report:', err);
      alert('Failed to export report');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Inventory Report
        </Typography>
        <Button
          variant="contained"
          color="success"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
        >
          Export to CSV
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          p: 2,
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6">
            Filters
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="warehouse-filter-label">Warehouse</InputLabel>
                <Select
                  labelId="warehouse-filter-label"
                  id="warehouse-filter"
                  name="warehouse_id"
                  value={filters.warehouse_id}
                  onChange={handleFilterChange}
                  label="Warehouse"
                >
                  <MenuItem value="">All Warehouses</MenuItem>
                  {warehouses.map(warehouse => (
                    <MenuItem key={warehouse.id} value={warehouse.id.toString()}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="part-type-filter-label">Part Type</InputLabel>
                <Select
                  labelId="part-type-filter-label"
                  id="part-type-filter"
                  name="part_type"
                  value={filters.part_type}
                  onChange={handleFilterChange}
                  label="Part Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="stock">Stock</MenuItem>
                  <MenuItem value="special_order">Special Order</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    bgcolor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <InventoryIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Parts
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {report.summary.total_parts}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    bgcolor: 'info.main',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <AssessmentIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Quantity
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {report.summary.total_quantity}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    bgcolor: 'success.main',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <AttachMoneyIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Value
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    ${report.summary.total_value.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    bgcolor: 'secondary.main',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <WarehouseIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Warehouses
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {Object.keys(report.summary.warehouses).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Inventory Details Table */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{
              p: 2,
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <InventoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">
                Inventory Details
              </Typography>
            </Box>

            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>Part Number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Warehouse</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Total Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.inventory.map(item => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <RouterLink
                          to={`/inventory/${item.part.id}`}
                          style={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            fontWeight: 500
                          }}
                        >
                          {item.part.internal_part_number}
                        </RouterLink>
                      </TableCell>
                      <TableCell>{item.part.description}</TableCell>
                      <TableCell>{item.location.warehouse.name}</TableCell>
                      <TableCell>{item.location.rack}-{item.location.shelf}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        ${item.part.cost ? parseFloat(item.part.cost).toFixed(2) : '0.00'}
                      </TableCell>
                      <TableCell align="right">
                        ${item.part.cost ? (parseFloat(item.part.cost) * item.quantity).toFixed(2) : '0.00'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          No inventory data available.
        </Alert>
      )}
    </Container>
  );
};

export default InventoryReport;
